import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import CierrePeriodoWidget from '../components/CierrePeriodoWidget';
import {
  BarChart3,
  PieChart,
  TrendingUp,
  Download,
  Calendar,
  AlertTriangle,
  Activity,
  Users,
  FileText,
  FileDown,
  BookOpen
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'];

export const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('MENSUAL');
  const [reportType, setReportType] = useState('alertas');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Datos para reportes
  const [alertasData, setAlertasData] = useState([]);
  const [monitoresData, setMonitoresData] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    loadReportData();
  }, [periodo]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      // Cargar alertas
      const alertsResponse = await api.get('/alerts?limit=500');
      const alerts = alertsResponse.data;

      // Cargar monitores
      const monitorsResponse = await api.get('/monitors');
      const monitors = monitorsResponse.data;

      // Procesar datos para gráficos
      processAlertData(alerts);
      processMonitorData(monitors, alerts);
      calculateStats(alerts, monitors);

    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar datos del reporte');
    } finally {
      setLoading(false);
    }
  };

  const processAlertData = (alerts) => {
    // Alertas por estado
    const porEstado = {};
    const porSeveridad = {};
    const porMes = {};

    alerts.forEach(alert => {
      // Por estado
      porEstado[alert.estado] = (porEstado[alert.estado] || 0) + 1;
      
      // Por severidad
      porSeveridad[alert.severidad] = (porSeveridad[alert.severidad] || 0) + 1;
      
      // Por mes
      const fecha = new Date(alert.created_at);
      const mes = fecha.toLocaleDateString('es-AR', { month: 'short', year: '2-digit' });
      porMes[mes] = (porMes[mes] || 0) + 1;
    });

    setAlertasData({
      porEstado: Object.entries(porEstado).map(([name, value]) => ({ name, value })),
      porSeveridad: Object.entries(porSeveridad).map(([name, value]) => ({ name, value })),
      porMes: Object.entries(porMes).map(([name, value]) => ({ name, value })).slice(-12)
    });
  };

  const processMonitorData = (monitors, alerts) => {
    // Alertas generadas por monitor
    const alertasPorMonitor = {};
    alerts.forEach(alert => {
      if (alert.monitor_id) {
        const monitor = monitors.find(m => m.id === alert.monitor_id);
        const nombre = monitor?.nombre || 'Sin monitor';
        alertasPorMonitor[nombre] = (alertasPorMonitor[nombre] || 0) + 1;
      }
    });

    setMonitoresData({
      alertasPorMonitor: Object.entries(alertasPorMonitor)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10),
      monitoresActivos: monitors.filter(m => m.activo).length,
      totalMonitores: monitors.length
    });
  };

  const calculateStats = (alerts, monitors) => {
    const ahora = new Date();
    const hace7Dias = new Date(ahora - 7 * 24 * 60 * 60 * 1000);
    const hace30Dias = new Date(ahora - 30 * 24 * 60 * 60 * 1000);

    const alertasUltimos7Dias = alerts.filter(a => new Date(a.created_at) > hace7Dias).length;
    const alertasUltimos30Dias = alerts.filter(a => new Date(a.created_at) > hace30Dias).length;
    const alertasAbiertas = alerts.filter(a => !['CERRADA', 'DESCARTADA'].includes(a.estado)).length;
    const alertasCriticas = alerts.filter(a => a.severidad === 'CRITICA').length;

    setStats({
      totalAlertas: alerts.length,
      alertasUltimos7Dias,
      alertasUltimos30Dias,
      alertasAbiertas,
      alertasCriticas,
      promedioSemanal: Math.round(alertasUltimos30Dias / 4),
      tasaResolucion: alerts.length > 0 
        ? Math.round((alerts.filter(a => a.estado === 'CERRADA').length / alerts.length) * 100)
        : 0,
      monitoresActivos: monitors.filter(m => m.activo).length
    });
  };

  const handleExportPDF = async (includeDetails = false) => {
    setExportLoading(true);
    try {
      const response = await api.get('/exports/reports/pdf', {
        params: {
          include_details: includeDetails,
          periodo: periodo
        },
        responseType: 'blob'
      });
      
      // Crear URL y descargar
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const tipo = includeDetails ? 'completo' : 'resumen';
      const fecha = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.download = `reporte_alertguard_${tipo}_${fecha}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Reporte PDF ${includeDetails ? 'completo' : 'resumido'} descargado`);
      setShowExportDialog(false);
    } catch (error) {
      console.error('Error descargando PDF:', error);
      toast.error('Error al generar el reporte PDF');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadManual = async () => {
    try {
      toast.info('Generando manual...');
      const response = await api.get('/exports/manual/pdf', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const fecha = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.download = `Manual_AlertGuard_${fecha}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Manual descargado correctamente');
    } catch (error) {
      console.error('Error descargando manual:', error);
      toast.error('Error al generar el manual');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando reportes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Reportes e Informes
          </h1>
          <p className="text-slate-600 mt-1">
            Análisis y métricas del sistema de alertas
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-40" style={{ backgroundColor: '#ffffff' }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SEMANAL">Última Semana</SelectItem>
              <SelectItem value="MENSUAL">Último Mes</SelectItem>
              <SelectItem value="TRIMESTRAL">Último Trimestre</SelectItem>
              <SelectItem value="ANUAL">Último Año</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={handleDownloadManual}
            variant="outline"
            style={{ borderColor: '#e2e8f0', color: '#475569' }}
            data-testid="download-manual-btn"
          >
            <BookOpen size={16} className="mr-2" />
            Manual
          </Button>
          <Button
            onClick={() => setShowExportDialog(true)}
            style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
            data-testid="export-pdf-btn"
          >
            <Download size={16} className="mr-2" />
            Exportar PDF
          </Button>
        </div>
      </div>

      {/* Dialog para elegir tipo de exportación */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-md" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="text-slate-900">Exportar Reporte PDF</DialogTitle>
            <DialogDescription className="text-slate-600">
              Elige el tipo de reporte que deseas descargar
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Card 
              className="p-4 cursor-pointer hover:border-blue-500 transition-colors border-2"
              onClick={() => !exportLoading && handleExportPDF(false)}
              data-testid="export-summary-option"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText size={24} className="text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Solo KPIs y Gráficos</h4>
                  <p className="text-sm text-slate-500">
                    Incluye indicadores clave, distribución por estado/severidad, 
                    top monitores y resumen ejecutivo.
                  </p>
                </div>
              </div>
            </Card>
            
            <Card 
              className="p-4 cursor-pointer hover:border-blue-500 transition-colors border-2"
              onClick={() => !exportLoading && handleExportPDF(true)}
              data-testid="export-detailed-option"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileDown size={24} className="text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Reporte Completo</h4>
                  <p className="text-sm text-slate-500">
                    Todo lo anterior más tablas detalladas con las últimas 50 alertas
                    y lista de monitores activos.
                  </p>
                </div>
              </div>
            </Card>
          </div>
          {exportLoading && (
            <div className="flex items-center justify-center py-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
              <span className="text-slate-600">Generando PDF...</span>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowExportDialog(false)}
              disabled={exportLoading}
              style={{ color: '#374151' }}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-xl">
              <AlertTriangle size={24} className="text-red-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{stats.totalAlertas}</p>
              <p className="text-sm text-slate-500">Total Alertas</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-xl">
              <TrendingUp size={24} className="text-orange-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{stats.alertasUltimos7Dias}</p>
              <p className="text-sm text-slate-500">Últimos 7 días</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-xl">
              <Activity size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{stats.tasaResolucion}%</p>
              <p className="text-sm text-slate-500">Tasa Resolución</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Activity size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{stats.monitoresActivos}</p>
              <p className="text-sm text-slate-500">Monitores Activos</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Alertas por Estado */}
        <Card className="p-6 bg-white border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <PieChart size={20} className="text-blue-600" />
            Alertas por Estado
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPie>
                <Pie
                  data={alertasData.porEstado}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {alertasData.porEstado?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Alertas por Severidad */}
        <Card className="p-6 bg-white border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 size={20} className="text-red-600" />
            Alertas por Severidad
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={alertasData.porSeveridad}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Tendencia de Alertas */}
        <Card className="p-6 bg-white border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp size={20} className="text-green-600" />
            Tendencia de Alertas
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={alertasData.porMes}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#3b82f6" 
                  fill="#93c5fd" 
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Top Monitores */}
        <Card className="p-6 bg-white border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Activity size={20} className="text-purple-600" />
            Top Monitores (por alertas generadas)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monitoresData.alertasPorMonitor} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  tick={{ fontSize: 10 }} 
                  width={120}
                />
                <Tooltip />
                <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Tabla Resumen */}
      <Card className="p-6 bg-white border-slate-200">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <FileText size={20} className="text-slate-600" />
          Resumen Ejecutivo
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-slate-50 rounded-lg">
            <h4 className="text-sm font-medium text-slate-500 mb-2">Métricas de Alertas</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between">
                <span className="text-slate-600">Total histórico:</span>
                <span className="font-bold text-slate-900">{stats.totalAlertas}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-slate-600">Abiertas actualmente:</span>
                <span className="font-bold text-orange-600">{stats.alertasAbiertas}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-slate-600">Críticas:</span>
                <span className="font-bold text-red-600">{stats.alertasCriticas}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-slate-600">Promedio semanal:</span>
                <span className="font-bold text-slate-900">{stats.promedioSemanal}</span>
              </li>
            </ul>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <h4 className="text-sm font-medium text-slate-500 mb-2">Indicadores de Gestión</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between">
                <span className="text-slate-600">Tasa de resolución:</span>
                <span className="font-bold text-green-600">{stats.tasaResolucion}%</span>
              </li>
              <li className="flex justify-between">
                <span className="text-slate-600">Monitores activos:</span>
                <span className="font-bold text-slate-900">{stats.monitoresActivos}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-slate-600">Últimos 30 días:</span>
                <span className="font-bold text-slate-900">{stats.alertasUltimos30Dias}</span>
              </li>
            </ul>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Acciones Recomendadas</h4>
            <ul className="space-y-2 text-sm text-blue-700">
              {stats.alertasAbiertas > 10 && (
                <li>• Revisar las {stats.alertasAbiertas} alertas abiertas</li>
              )}
              {stats.alertasCriticas > 0 && (
                <li>• Atender {stats.alertasCriticas} alertas críticas pendientes</li>
              )}
              {stats.tasaResolucion < 70 && (
                <li>• Mejorar tasa de resolución (actual: {stats.tasaResolucion}%)</li>
              )}
              {stats.alertasAbiertas <= 10 && stats.alertasCriticas === 0 && stats.tasaResolucion >= 70 && (
                <li>✓ Sistema en buen estado</li>
              )}
            </ul>
          </div>
        </div>
      </Card>

      {/* Widget de Cierre de Período */}
      <CierrePeriodoWidget className="mt-6" />
    </div>
  );
};

export default Reports;
